import type { ToolCategory, ToolDefinition, ToolInput } from './tools';
import { deterministicGenerators } from './tools';
import {
  estimateProjectCost,
  isProjectCostProfile,
  type ProjectCostEstimate,
} from '@/lib/projectCostEstimate';
import {
  PROJECT_COST_PROFILE_LABEL,
  PROJECT_COST_PROFILE_ORDER,
  formatProjectCostBandLine,
} from '@/lib/projectCostBands.shared.js';

export type ToolsLang = 'tr' | 'en';

type InputOverlay = {
  label?: string;
  placeholder?: string;
  optionLabels?: string[];
};

type ToolOverlay = {
  segment?: string;
  title?: string;
  tagline?: string;
  intro?: string;
  inputs?: Record<string, InputOverlay>;
  seo?: ToolDefinition['seo'];
  howItWorks?: string[];
  useCases?: string[];
  faq?: { q: string; a: string }[];
  example?: ToolDefinition['example'];
};

export type LocalizedToolInput = ToolInput & { optionLabels?: string[] };

export const categoryLabelsByLang: Record<ToolsLang, Record<ToolCategory, string>> = {
  tr: {
    content: 'Metin & İçerik',
    design: 'Tasarım & Marka',
    business: 'İş & Strateji',
    local: 'Yerel İşletme',
    career: 'Kariyer',
  },
  en: {
    content: 'Copy & Content',
    design: 'Design & Brand',
    business: 'Business & Strategy',
    local: 'Local Business',
    career: 'Career',
  },
};

export function getCategoryLabel(category: ToolCategory, lang: ToolsLang): string {
  return categoryLabelsByLang[lang][category];
}

export const toolsPageCopy: Record<
  ToolsLang,
  {
    seoTitle: string;
    seoDescription: string;
    tag: string;
    title: string;
    description: string;
    breadcrumbHome: string;
    breadcrumbTools: string;
    backToTools: string;
    badgeFree: string;
    generate: string;
    generating: string;
    howToTitle: (toolTitle: string) => string;
    sectionHowItWorks: string;
    sectionUseCases: string;
    sectionExample: string;
    sectionFaq: string;
    sectionRelated: string;
    studioBadge: string;
    studioTitle: string;
    studioBody: string;
    studioAboutLink: string;
    studioServicesLink: string;
    ctaTitle: string;
    ctaBody: string;
    ctaButton: string;
    loadingAi: string;
    loadingCalc: string;
    errorUnexpected: string;
    errorAiNotConfigured: string;
    errorConnection: string;
    errorUnexpectedResponse: string;
    errorRateLimit: string;
    errorGeneric: string;
    errorEmpty: string;
  }
> = {
  tr: {
    seoTitle: 'Ücretsiz AI Araçları — Üyelik Gerektirmez',
    seoDescription:
      'Ücretsiz AI araçları: ATS CV analizi, iş ilanı yazıcı, fiyat teklifi, motivasyon mektubu, Meta reklam metni ve daha fazlası. Kayıt olmadan tarayıcıdan kullanın.',
    tag: 'Figtures Product Studio',
    title: 'Gerçek AI Araçları',
    description:
      "Figtures Product Studio'nun ücretsiz yapay zeka araçları — her biri belirli bir işe özel. Web'de kayıtsız deneyin; aynı mantığı uygulamanıza, CRM'inize veya MVP'nize entegre etmek için ekibimizle konuşun.",
    breadcrumbHome: 'Ana Sayfa',
    breadcrumbTools: 'AI Araçları',
    backToTools: 'Araçlar',
    badgeFree: 'Ücretsiz • Kayıt yok',
    generate: 'Seçenekleri üret',
    generating: 'Üretiliyor...',
    howToTitle: (toolTitle) => `${toolTitle} nasıl kullanılır?`,
    sectionHowItWorks: 'Nasıl çalışır?',
    sectionUseCases: 'Kullanım alanları',
    sectionExample: 'Örnek çıktı',
    sectionFaq: 'Sıkça sorulan sorular',
    sectionRelated: 'İlgili araçlar',
    studioBadge: 'Figtures Product Studio',
    studioTitle: 'Özel yazılım ve AI product studio',
    studioBody:
      'Bu araç Figtures tarafından geliştirildi — AI geliştirme, özel yazılım ve SaaS/MVP teslimatı yapan bir product studio. Web sürümü ücretsiz; işletmenize özel entegrasyon veya mobil uygulama için konuşalım.',
    studioAboutLink: 'Figtures kimdir?',
    studioServicesLink: 'Hizmetlerimiz',
    ctaTitle: 'Bunu uygulamanıza entegre edelim mi?',
    ctaBody:
      'Web\'de ücretsiz denediniz. Aynı iş akışını CRM\'inize, mobil uygulamanıza veya dahili panelinize özel geliştirebiliriz.',
    ctaButton: 'Özel uygulama teklifi al',
    loadingAi: 'AI seçenekleri hazırlanıyor...',
    loadingCalc: 'Hesaplanıyor...',
    errorUnexpected: 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.',
    errorAiNotConfigured: 'AI servisi henüz yapılandırılmadı. Lütfen daha sonra tekrar deneyin.',
    errorConnection: 'Bağlantı kurulamadı. İnternet bağlantınızı kontrol edin.',
    errorUnexpectedResponse: 'Beklenmeyen bir yanıt alındı. Tekrar deneyin.',
    errorRateLimit: 'Çok fazla istek gönderildi. Lütfen bir dakika sonra tekrar deneyin.',
    errorGeneric: 'Bir hata oluştu. Tekrar deneyin.',
    errorEmpty: 'Sonuç üretilemedi. Girdilerinizi kontrol edip tekrar deneyin.',
  },
  en: {
    seoTitle: 'Free AI Tools — No Sign-Up Required',
    seoDescription:
      'Free AI tools: ATS CV scoring, job posting writer, price quotes, cover letters, Meta ad copy, and more. Run them straight from your browser—no account needed.',
    tag: 'Figtures Product Studio',
    title: 'Purpose-built AI tools',
    description:
      'Free AI tools from Figtures Product Studio—each built for a specific job. Try them on the web with no signup; ask us to integrate the same workflow into your app, CRM, or MVP.',
    breadcrumbHome: 'Home',
    breadcrumbTools: 'AI Tools',
    backToTools: 'Tools',
    badgeFree: 'Free • No signup',
    generate: 'Generate options',
    generating: 'Generating...',
    howToTitle: (toolTitle) => `How to use ${toolTitle}`,
    sectionHowItWorks: 'How it works',
    sectionUseCases: 'Use cases',
    sectionExample: 'Sample output',
    sectionFaq: 'Frequently asked questions',
    sectionRelated: 'Related tools',
    studioBadge: 'Figtures Product Studio',
    studioTitle: 'Custom software & AI product studio',
    studioBody:
      'Built by Figtures—a product studio for AI development, custom software, and SaaS/MVP delivery. This web tool is free; talk to us about a version integrated into your business.',
    studioAboutLink: 'What is Figtures?',
    studioServicesLink: 'Our services',
    ctaTitle: 'Want this inside your product?',
    ctaBody:
      'You tried it free on the web. We can build the same workflow into your CRM, mobile app, or internal dashboard.',
    ctaButton: 'Request a custom build',
    loadingAi: 'Preparing AI options...',
    loadingCalc: 'Calculating...',
    errorUnexpected: 'Something went wrong. Please try again.',
    errorAiNotConfigured: 'The AI service is not configured yet. Please try again later.',
    errorConnection: 'Could not connect. Check your internet connection.',
    errorUnexpectedResponse: 'Unexpected response. Please try again.',
    errorRateLimit: 'Too many requests. Please wait a minute and try again.',
    errorGeneric: 'Something went wrong. Please try again.',
    errorEmpty: 'No results were generated. Check your inputs and try again.',
  },
};

export const toolsResultsCopy: Record<
  ToolsLang,
  {
    loadingHint: string;
    readyTitle: string;
    readySubtitle: string;
    copyAll: string;
    save: string;
    regenerate: string;
    shareLabel: string;
    copyShareText: string;
    copied: string;
    copy: string;
    toastCopiedOne: string;
    toastCopiedAll: string;
    toastSavedTitle: string;
    toastSavedDesc: string;
    toastShareCopied: string;
    savedSection: (n: number) => string;
    savedSessionNote: string;
    defaultLoading: string;
    optionLabel: (n: number) => string;
  }
> = {
  tr: {
    loadingHint: 'Birkaç saniye içinde birbirinden farklı seçenekler hazır olacak…',
    readyTitle: 'Hazır — kopyala, kaydet veya paylaş',
    readySubtitle: "ChatGPT'de prompt yazmak yerine doğrudan kullanıma hazır seçenekler.",
    copyAll: 'Tümünü kopyala',
    save: 'Kaydet',
    regenerate: 'Başka alternatifler',
    shareLabel: 'Arkadaşına öner:',
    copyShareText: 'Metni kopyala',
    copied: 'Kopyalandı',
    copy: 'Kopyala',
    toastCopiedOne: 'Panoya kopyalandı',
    toastCopiedAll: 'Tüm sonuçlar kopyalandı',
    toastSavedTitle: 'Kaydedildi',
    toastSavedDesc: 'Sonuçlar tarayıcınızda saklandı.',
    toastShareCopied: 'Paylaşım metni kopyalandı',
    savedSection: (n) => `Kaydedilenler (${n})`,
    savedSessionNote: 'Bu oturumda kaydedildi — tarayıcı verilerini silene kadar saklanır.',
    defaultLoading: 'Üretiliyor...',
    optionLabel: (n) => `Seçenek ${n}`,
  },
  en: {
    loadingHint: 'A few different options will be ready in seconds…',
    readyTitle: 'Ready — copy, save, or share',
    readySubtitle: 'Ready-to-use options—no prompt engineering in ChatGPT.',
    copyAll: 'Copy all',
    save: 'Save',
    regenerate: 'More alternatives',
    shareLabel: 'Share with a colleague:',
    copyShareText: 'Copy share text',
    copied: 'Copied',
    copy: 'Copy',
    toastCopiedOne: 'Copied to clipboard',
    toastCopiedAll: 'All results copied',
    toastSavedTitle: 'Saved',
    toastSavedDesc: 'Results are stored in your browser.',
    toastShareCopied: 'Share text copied',
    savedSection: (n) => `Saved (${n})`,
    savedSessionNote: 'Saved this session—kept until you clear browser data.',
    defaultLoading: 'Generating...',
    optionLabel: (n) => `Option ${n}`,
  },
};

const resultLabelsByLang: Record<ToolsLang, Record<string, string[]>> = {
  tr: {
    'marketplace-product-description': ['Satış odaklı', 'SEO güçlü', 'Hikâye anlatımı'],
    'google-review-responder': ['Samimi', 'Profesyonel', 'Çözüm odaklı'],
    'menu-description-generator': ['Betimleyici', 'Kısa & net', 'Premium'],
    'real-estate-listing-writer': ['Aile dostu', 'Yatırımcı', 'Lüks yaşam'],
    'pitch-evaluator': ['Genel skor', 'Güçlü yönler', 'Riskler', 'Zor sorular', 'Geliştirilmiş pitch'],
    'aso-description-generator': ['Başlıklar', 'Kısa açıklama', 'Uzun açıklama'],
    'reels-script-generator': ['Hook fikirleri', '30 sn senaryo', 'CTA & hashtag'],
    'linkedin-about-generator': ['LinkedIn Hakkında', 'Niyet mektubu'],
    'brand-kit-generator': ['Marka isimleri', 'Slogan', 'Renk paleti', 'Marka sesi', 'Domainler'],
    'customer-reply-generator': ['Resmi', 'Samimi', 'Kısa & net'],
    'roi-calculator': ['E-posta', 'Sosyal medya', 'SEO', 'Toplam tahmin'],
    'project-cost-estimator': ['MVP', 'Full-stack', 'UI/UX', 'Bakım'],
    'color-palette-generator': ['Minimal', 'Cesur', 'Doğal', 'Teknolojik'],
    'ats-cv-analyzer': ['ATS Skoru', 'Eksikler', 'Güçlü yönler', 'İyileştirilmiş maddeler'],
    'job-application-writer': ['CV maddeleri', 'Ön yazı — samimi', 'Ön yazı — profesyonel'],
    'presentation-outline-generator': ['Sunum taslağı'],
    'youtube-seo-generator': ['Başlıklar', 'Açıklama', 'Etiketler'],
    'instagram-caption-generator': ['Caption', 'Hashtagler', 'Bio satırı'],
    'job-posting-writer': ['İlan metni', 'Kısa özet', 'ATS anahtar kelimeler', 'Dahil edici dil kontrolü'],
    'quote-proposal-writer': ['Teklif mektubu', 'Kapsam tablosu', 'Fiyat & ödeme', 'Hariç tutulanlar'],
    'motivation-letter-writer': ['Akademik', 'Kişisel & hikâye', 'Kısa & net'],
    'meta-ads-copy-generator': ['Set 1 — Merak hook', 'Set 2 — Fayda hook', 'Set 3 — Sosyal kanıt hook', 'Hook analizi'],
  },
  en: {
    'marketplace-product-description': ['Conversion-focused', 'SEO-strong', 'Story-driven'],
    'google-review-responder': ['Warm', 'Professional', 'Solution-oriented'],
    'menu-description-generator': ['Descriptive', 'Short & clear', 'Premium'],
    'real-estate-listing-writer': ['Family-friendly', 'Investor', 'Luxury lifestyle'],
    'pitch-evaluator': ['Overall score', 'Strengths', 'Risks', 'Hard questions', 'Refined pitch'],
    'aso-description-generator': ['Titles', 'Short description', 'Long description'],
    'reels-script-generator': ['Hook ideas', '30s script', 'CTA & hashtags'],
    'linkedin-about-generator': ['LinkedIn About', 'Cover letter'],
    'brand-kit-generator': ['Brand names', 'Tagline', 'Color palette', 'Brand voice', 'Domains'],
    'customer-reply-generator': ['Formal', 'Friendly', 'Short & clear'],
    'roi-calculator': ['Email', 'Social ads', 'SEO', 'Total estimate'],
    'project-cost-estimator': ['MVP', 'Full-stack', 'UI/UX', 'Maintenance'],
    'color-palette-generator': ['Minimal', 'Bold', 'Natural', 'Tech'],
    'ats-cv-analyzer': ['ATS Score', 'Gaps', 'Strengths', 'Improved bullets'],
    'job-application-writer': ['CV bullets', 'Cover letter — warm', 'Cover letter — formal'],
    'presentation-outline-generator': ['Deck outline'],
    'youtube-seo-generator': ['Titles', 'Description', 'Tags'],
    'instagram-caption-generator': ['Caption', 'Hashtags', 'Bio line'],
    'job-posting-writer': ['Job posting', 'Short summary', 'ATS keywords', 'Inclusive language check'],
    'quote-proposal-writer': ['Proposal letter', 'Scope table', 'Price & payment', 'Exclusions'],
    'motivation-letter-writer': ['Academic', 'Personal & story', 'Short & clear'],
    'meta-ads-copy-generator': ['Set 1 — Curiosity hook', 'Set 2 — Benefit hook', 'Set 3 — Social proof hook', 'Hook analysis'],
  },
};

export function labelForToolResult(
  toolId: string,
  index: number,
  lang: ToolsLang,
  parsed?: string,
): string {
  if (parsed?.trim()) return parsed.trim();
  const defaults = resultLabelsByLang[lang][toolId];
  if (defaults?.[index]) return defaults[index];
  return toolsResultsCopy[lang].optionLabel(index + 1);
}

const AI_ERROR_MAP: Record<string, keyof (typeof toolsPageCopy)['en']> = {
  'AI servisi henüz yapılandırılmadı. Lütfen daha sonra tekrar deneyin.': 'errorAiNotConfigured',
  'Bağlantı kurulamadı. İnternet bağlantınızı kontrol edin.': 'errorConnection',
  'Beklenmeyen bir yanıt alındı. Tekrar deneyin.': 'errorUnexpectedResponse',
  'Bir hata oluştu. Tekrar deneyin.': 'errorGeneric',
  'Sonuç üretilemedi. Girdilerinizi kontrol edip tekrar deneyin.': 'errorEmpty',
  'Sonuç üretilemedi. Tekrar deneyin.': 'errorEmpty',
};

export function localizeAiErrorMessage(message: string, lang: ToolsLang): string {
  if (lang === 'tr') return message;
  if (message.includes('Çok fazla istek') || message.includes('bir dakika')) {
    return toolsPageCopy.en.errorRateLimit;
  }
  const key = AI_ERROR_MAP[message];
  if (key) return toolsPageCopy.en[key];
  return message;
}

const toolsEn: Record<string, ToolOverlay> = {
  'marketplace-product-description': {
    segment: 'E-commerce sellers',
    title: 'Marketplace Product Description & SEO Generator',
    tagline: 'Conversion-ready copy for Trendyol, Hepsiburada, Etsy, and more',
    intro:
      'Enter your product details and get marketplace-ready, SEO-friendly descriptions in seconds. Output is tailored for Trendyol, Hepsiburada, Amazon, and Etsy formats.',
    inputs: {
      product: { label: 'Product name', placeholder: 'e.g. Cotton oversized tee' },
      features: { label: 'Key features', placeholder: 'e.g. 100% cotton, unisex, 5 colors, front print' },
      marketplace: {
        optionLabels: ['Trendyol', 'Hepsiburada', 'Amazon', 'Etsy'],
      },
    },
    seo: {
      title: 'Marketplace Product Description Generator — Free AI Tool',
      description:
        'Create SEO-friendly, sales-focused product descriptions for Trendyol, Hepsiburada, and Etsy in seconds—free, no signup.',
      keywords: ['product description generator', 'marketplace listing copy', 'ecommerce seo', 'etsy product description'],
    },
    howItWorks: [
      'Enter the product name and standout features.',
      'Choose your marketplace.',
      'Get three description variants plus keyword suggestions.',
    ],
    useCases: [
      'Launch new listings with professional copy fast',
      'Refresh existing listings for SEO',
      'Batch-write descriptions for large catalogs',
    ],
    faq: [
      {
        q: 'Are the descriptions actually SEO-friendly?',
        a: 'Yes—each output includes keyword suggestions to help you rank in marketplace search.',
      },
      {
        q: 'Which marketplaces are supported?',
        a: 'Formats are optimized for Trendyol, Hepsiburada, Amazon, and Etsy.',
      },
      { q: 'Is it free?', a: 'Yes. The tool is free and requires no account.' },
    ],
    example: {
      summary: 'Cotton oversized tee · Trendyol',
      results: [
        '100% Cotton Oversized Tee — Everyday Comfort, Elevated\nBreathable cotton keeps you comfortable all day. A unisex oversized fit and five color options pair with any wardrobe.\n• 100% cotton, moisture-friendly fabric\n• Unisex oversized fit\n• 5 color options\n• Fade-resistant front print\nKeywords: oversized tee, cotton t-shirt, unisex tee',
      ],
    },
  },
  'google-review-responder': {
    segment: 'Local businesses & SMBs',
    title: 'Google Review Response Generator',
    tagline: 'Professional, human replies to customer reviews',
    intro:
      'Generate thoughtful responses to positive or negative Google Business Profile reviews in seconds. Protect your reputation with consistent review management.',
    inputs: {
      business: { label: 'Business name / type', placeholder: 'e.g. Lavender Café' },
      review: { label: 'Customer review', placeholder: 'Paste the review here...' },
      tone: {
        optionLabels: ['Warm & professional', 'Formal', 'Warm & grateful', 'Solution-focused'],
      },
    },
    seo: {
      title: 'Google Review Response Generator — Reply to Customers',
      description:
        'Write professional Google Business review replies with free AI—for positive and negative feedback alike.',
      keywords: ['google review response', 'reply to customer review', 'google business profile', 'negative review reply'],
    },
    howItWorks: [
      'Enter your business name and the review text.',
      'Pick the tone you want.',
      'Copy any of three ready-to-post replies.',
    ],
    useCases: [
      'Respond to negative reviews with empathy',
      'Thank customers for positive feedback',
      'Speed up review management workflows',
    ],
    faq: [
      {
        q: 'How does it handle negative reviews?',
        a: 'It avoids defensiveness, acknowledges the issue, and offers a constructive next step.',
      },
      {
        q: 'Can I edit the replies?',
        a: 'Absolutely—copy the text and adjust it to match your voice.',
      },
    ],
    example: {
      summary: 'Lavender Café · negative review',
      results: [
        'Thank you for sharing your feedback. We are sorry your visit fell short of what we aim to deliver—that is not the experience we want for our guests. We are reviewing this with our team and will take action. We would love the chance to welcome you back and make it right.',
      ],
    },
  },
  'menu-description-generator': {
    segment: 'Restaurants & cafés',
    title: 'AI Menu Description Generator',
    tagline: 'Appetizing menu copy + English translation',
    intro:
      'Turn dish names into mouthwatering menu descriptions—with English translations so tourist and expat guests feel welcome.',
    inputs: {
      dish: { label: 'Dish / drink name', placeholder: 'e.g. Truffle mushroom risotto' },
      ingredients: { label: 'Ingredients', placeholder: 'e.g. arborio rice, mushrooms, truffle oil, parmesan' },
    },
    seo: {
      title: 'Menu Description Writer — Free AI Menu Copy',
      description:
        'Create appetizing restaurant menu descriptions with English translations—free AI, no signup.',
      keywords: ['menu description', 'restaurant menu copy', 'menu writing examples', 'food description generator'],
    },
    howItWorks: [
      'Enter the dish name and ingredients.',
      'Click generate.',
      'Get three description styles with English versions.',
    ],
    useCases: ['New menu launches', 'Bilingual QR menus', 'Translate items for international guests'],
    faq: [
      { q: 'Is the English translation automatic?', a: 'Yes—each description includes an English line underneath.' },
      { q: 'How many variants do I get?', a: 'Three distinct styles every time you generate.' },
    ],
    example: {
      summary: 'Truffle mushroom risotto',
      results: [
        'Creamy arborio rice with fresh mushrooms and real truffle oil, finished with parmesan—an Italian classic in every spoonful.\n(Creamy arborio rice with fresh mushrooms and real truffle oil, crowned with parmesan.)',
      ],
    },
  },
  'real-estate-listing-writer': {
    segment: 'Real estate agents',
    title: 'Real Estate Listing Copy Generator',
    tagline: 'Listings that highlight location and lifestyle',
    intro:
      'Add property details and receive polished listing copy that sells the neighborhood, layout, and lifestyle—not just square meters.',
    inputs: {
      type: {
        optionLabels: ['For sale — apartment', 'For rent — apartment', 'For sale — villa', 'For sale — land', 'Commercial'],
      },
      location: { label: 'Location', placeholder: 'e.g. Kadıköy, Moda, Istanbul' },
      details: {
        label: 'Details',
        placeholder: 'e.g. 3 bed, 140 m², 2nd floor, sea view, new build',
      },
    },
    seo: {
      title: 'Real Estate Listing Description Generator — Free AI',
      description:
        'Write compelling property listings for sale and rent—free AI copy that emphasizes location and lifestyle.',
      keywords: ['real estate listing description', 'property ad copy', 'apartment listing example', 'listing writer'],
    },
    howItWorks: [
      'Choose listing type and location.',
      'Add property details.',
      'Get three publish-ready listing texts.',
    ],
    useCases: ['Portal listings', 'Social posts', 'Window displays and brochures'],
    faq: [
      {
        q: 'Which sites is the copy suited for?',
        a: 'Generic, portal-friendly format—works on major listing sites and classifieds.',
      },
      { q: 'Does it overpromise?', a: 'No—it stays credible and trust-building while still persuasive.' },
    ],
    example: {
      summary: 'For sale · Kadıköy Moda · 3 bed',
      results: [
        'Sea-View 3-Bed in the Heart of Moda\nSteps from the coast in Kadıköy Moda, this 140 m² home balances calm and city energy. A generous balcony and sea views meet cafés, the waterfront, and transit nearby.',
      ],
    },
  },
  'pitch-evaluator': {
    segment: 'Startup founders',
    title: 'AI Investor Pitch Evaluator',
    tagline: 'Honest VC-style feedback on your idea',
    intro:
      'Stress-test your startup idea like an investor would: score out of 100, strengths and gaps, tough questions, and a sharper pitch draft.',
    inputs: {
      idea: { label: 'Your startup idea', placeholder: 'Describe your idea in 2–3 sentences...' },
      market: { label: 'Target customer / market', placeholder: 'e.g. accounting automation for SMBs' },
    },
    seo: {
      title: 'Startup Idea Evaluation — AI Pitch Review Tool',
      description:
        'Free AI pitch review: score, strengths, risks, investor questions, and an improved pitch—before you walk into the room.',
      keywords: ['startup idea evaluation', 'investor pitch', 'pitch deck feedback', 'vc pitch review'],
    },
    howItWorks: [
      'Describe your idea and target market.',
      'Run the investor-style analysis.',
      'Review score, strengths, risks, hard questions, and a refined pitch.',
    ],
    useCases: ['Investor meeting prep', 'Idea validation', 'Pre–pitch deck feedback'],
    faq: [
      {
        q: 'How realistic is the evaluation?',
        a: 'It mirrors an experienced VC lens—direct about upside and risks.',
      },
      { q: 'Is my idea stored?', a: 'No—input is used only for the live analysis.' },
    ],
    example: {
      summary: 'AI accounting assistant for SMBs',
      results: [
        'Score: 72/100 — Clear pain point, scalable model, solid foundation with room to sharpen positioning.',
        'Strengths\n• Large, paying SMB segment\n• Fits recurring revenue\n• Credible time-savings promise with AI',
      ],
    },
  },
  'aso-description-generator': {
    segment: 'App & SaaS founders',
    title: 'App Store & Play Store ASO Copy Generator',
    tagline: 'Store listings built to drive installs',
    intro:
      'Describe your app and get ASO-ready titles, short descriptions, and keyword-rich long copy for the App Store and Google Play.',
    inputs: {
      app: { label: 'App name', placeholder: 'e.g. FitTrack' },
      description: {
        label: 'What does the app do?',
        placeholder: 'e.g. Fitness app for workouts and nutrition tracking',
      },
      platform: { optionLabels: ['Both', 'App Store', 'Google Play'] },
    },
    seo: {
      title: 'App Store Description & ASO Generator — Free',
      description:
        'Generate ASO titles, descriptions, and keywords for App Store and Google Play—free AI, more visibility, more downloads.',
      keywords: ['app store description', 'aso copy', 'play store listing', 'app store optimization'],
    },
    howItWorks: [
      'Enter app name and value proposition.',
      'Select target store(s).',
      'Get titles, short and long descriptions, and keywords.',
    ],
    useCases: ['New app launches', 'ASO ranking lifts', 'Store listing A/B copy'],
    faq: [
      { q: 'Does it respect character limits?', a: 'Yes—titles and short descriptions follow store limits.' },
      { q: 'Can I optimize for both stores?', a: 'Choose App Store, Google Play, or both.' },
    ],
    example: {
      summary: 'FitTrack · Both stores',
      results: ['Title ideas\n• FitTrack: Workouts & Nutrition\n• FitTrack — Fitness Journal\n• FitTrack: Train Smarter'],
    },
  },
  'reels-script-generator': {
    segment: 'Social media managers',
    title: 'Reels & TikTok Script Generator',
    tagline: 'Viral hooks, 30-second scripts, hashtags',
    intro:
      'Enter your topic and get scroll-stopping hooks, a scene-by-scene 30-second script, and a ready hashtag set.',
    inputs: {
      topic: { label: 'Video topic', placeholder: 'e.g. Home coffee brewing tips' },
      platform: { optionLabels: ['Instagram Reels', 'TikTok', 'YouTube Shorts'] },
      audience: { label: 'Target audience', placeholder: 'e.g. coffee lovers aged 20–30' },
    },
    seo: {
      title: 'Reels Script & TikTok Hook Generator — Free AI',
      description:
        'Create viral hooks, 30-second Reels/TikTok scripts, and hashtags with free AI.',
      keywords: ['reels script', 'tiktok hook ideas', 'short form video script', 'social media script'],
    },
    howItWorks: [
      'Enter topic and platform.',
      'Add your audience.',
      'Get hooks, script, and hashtags.',
    ],
    useCases: ['Fill your content calendar', 'Brainstorm viral angles', 'Campaign creative scripts'],
    faq: [
      {
        q: 'Why do hooks matter?',
        a: 'The first three seconds decide retention—the tool gives you five strong openers.',
      },
      { q: 'Are hashtags relevant?', a: 'Sets are tuned to your topic and platform for reach.' },
    ],
    example: {
      summary: 'Home coffee brewing · Instagram Reels',
      results: [
        'Hook ideas\n• "The one trick baristas never mention…"\n• "You\'re wasting your beans—here\'s proof 👇"\n• "You\'ll know what you did wrong in 3 seconds"\n• "Don\'t brew again until you see this"\n• "Barista quality at home? Let\'s test it."',
      ],
    },
  },
  'linkedin-about-generator': {
    segment: 'Job seekers & professionals',
    title: 'LinkedIn About & Cover Letter Generator',
    tagline: 'Profile summary and application copy that stand out',
    intro:
      'Add your role and experience—get a compelling LinkedIn About section and a role-specific cover letter recruiters actually read.',
    inputs: {
      role: { label: 'Role / title', placeholder: 'e.g. Digital Marketing Specialist' },
      experience: {
        label: 'Experience & skills',
        placeholder: 'e.g. 5 years SEO, Google Ads, content strategy, team lead',
      },
    },
    seo: {
      title: 'LinkedIn About & Cover Letter — Free AI Writer',
      description:
        'Write your LinkedIn About and cover letters with free AI—professional copy that gets attention.',
      keywords: ['linkedin about section', 'cover letter example', 'linkedin profile summary', 'cover letter writer'],
    },
    howItWorks: [
      'Enter the role you are targeting.',
      'Add experience and skills.',
      'Copy your About section and cover letter.',
    ],
    useCases: ['Job search refresh', 'LinkedIn profile update', 'Application cover text'],
    faq: [
      { q: 'Is it written in first person?', a: 'Yes—the About section uses natural first-person voice.' },
      { q: 'English versions?', a: 'Generate in English when your profile and market require it.' },
    ],
    example: {
      summary: 'Digital Marketing Specialist · 5 years',
      results: [
        'I help brands grow through data-informed marketing. Over five years I have led SEO, Google Ads, and content programs that doubled organic traffic for B2B clients. I like turning creative ideas into measurable pipeline.',
      ],
    },
  },
  'brand-kit-generator': {
    segment: 'Brand owners & founders',
    title: 'AI Brand Kit Generator',
    tagline: 'Name, tagline, colors, voice, domains—together',
    intro:
      'Describe your business and get a starter brand kit: memorable name ideas, tagline, color palette, voice guidelines, and domain suggestions.',
    inputs: {
      business: {
        label: 'Business description',
        placeholder: 'e.g. handmade natural soap brand',
      },
      vibe: { label: 'Brand feel / values', placeholder: 'e.g. natural, minimal, premium' },
    },
    seo: {
      title: 'Brand Name & Kit Generator — Free AI',
      description:
        'Generate names, taglines, color palettes, brand voice, and domain ideas—free AI brand starter kit.',
      keywords: ['brand name generator', 'brand identity', 'business name ideas', 'tagline generator'],
    },
    howItWorks: [
      'Describe the business and brand feel.',
      'Click generate.',
      'Review names, tagline, colors, voice, and domains.',
    ],
    useCases: ['Launch a new brand', 'Explore rebranding directions', 'Kick off marketing assets'],
    faq: [
      { q: 'Are colors in HEX?', a: 'Yes—ready-to-use four-color HEX palettes.' },
      { q: 'Are domains available?', a: 'Suggestions only—check availability on a registrar.' },
    ],
    example: {
      summary: 'Handmade natural soap · natural, minimal',
      results: [
        'Name ideas\n• Pure & Simple — clarity and purity\n• Lavé — elegant, memorable\n• Earth Soap — honest and warm\n• Pure Atelier — crafted premium feel\n• Foam & Field — descriptive and friendly',
      ],
    },
  },
  'customer-reply-generator': {
    segment: 'All businesses',
    title: 'Customer Message Reply Generator',
    tagline: 'Fast, professional WhatsApp and email replies',
    intro:
      'Paste an inbound customer message and get three replies—formal, friendly, and short—matched to intent and channel.',
    inputs: {
      message: { label: 'Incoming message', placeholder: 'Paste the customer message here...' },
      intent: {
        optionLabels: ['General question', 'Sales / pricing', 'Complaint', 'Booking / reservation', 'Return / exchange'],
      },
      channel: { optionLabels: ['WhatsApp', 'Email', 'Instagram DM'] },
    },
    seo: {
      title: 'Customer Message Reply Generator — Free AI',
      description:
        'Reply to WhatsApp and email customer messages with free AI—formal, friendly, and concise options.',
      keywords: ['customer message reply', 'whatsapp business reply', 'customer email response', 'support copy'],
    },
    howItWorks: [
      'Paste the customer message.',
      'Choose intent and channel.',
      'Pick from three tone variants.',
    ],
    useCases: ['Faster support', 'Answer sales questions', 'Handle complaints with care'],
    faq: [
      { q: 'How many tones?', a: 'Three replies per message: formal, friendly, and short & clear.' },
      { q: 'Complaints?', a: 'Empathetic, ownership-taking language with a clear next step.' },
    ],
    example: {
      summary: 'Sales / pricing · WhatsApp',
      results: [
        'Formal: Thank you for your interest. We are sharing current pricing and stock details with you now. If you have any other questions, we are happy to help.',
      ],
    },
  },
  'roi-calculator': {
    segment: 'Marketing & business owners',
    title: 'Digital Marketing ROI Calculator',
    tagline: 'See estimated returns on your budget instantly',
    intro:
      'Enter your monthly marketing budget and get channel-level ROI estimates—so you can allocate spend with clearer expectations.',
    inputs: {
      budget: { label: 'Monthly budget (TRY)', placeholder: 'e.g. 25000' },
    },
    seo: {
      title: 'Digital Marketing ROI Calculator — Free',
      description:
        'Estimate returns on SEO, social ads, and email with a free ROI calculator—plan your marketing budget.',
      keywords: ['roi calculator', 'marketing roi', 'digital marketing return', 'roas estimate'],
    },
    howItWorks: [
      'Enter monthly marketing budget.',
      'Click calculate.',
      'Review estimated returns by channel.',
    ],
    useCases: ['Budget planning', 'Channel comparison', 'Investment decisions'],
    faq: [
      {
        q: 'How is this calculated?',
        a: 'Industry-average benchmarks—real results will vary.',
      },
      { q: 'Is data stored?', a: 'No—all math runs in your browser.' },
    ],
    example: {
      summary: 'Monthly budget ₺25,000',
      results: [
        'Email marketing ROI: ₺105,000 — Highest return rate',
        'Social ad return: ₺52,500 — ROAS: 2.1x',
      ],
    },
  },
  'project-cost-estimator': {
    segment: 'Software & product teams',
    title: 'Software Project Cost Estimator',
    tagline: 'Ballpark build cost in seconds',
    intro:
      'Describe your project type and see rough ranges from MVP through full platform—cost and timeline at a glance.',
    inputs: {
      project: { label: 'Project type', placeholder: 'e.g. corporate website, ecommerce, SaaS, mobile app' },
      profile: {
        label: 'Project profile',
        optionLabels: [
          'Infer from my description',
          ...PROJECT_COST_PROFILE_ORDER.map((id) => PROJECT_COST_PROFILE_LABEL[id].en),
        ],
      },
    },
    seo: {
      title: 'Software Project Cost Estimator — Free',
      description:
        'Estimate software project cost—MVP, SaaS, ecommerce, and mobile ranges. Free budgeting tool.',
      keywords: ['software project cost', 'mvp cost estimate', 'app development budget', 'project estimate'],
    },
    howItWorks: [
      'Describe the project, or pick the project profile directly.',
      'Click calculate.',
      'Review cost and timeline ranges; if the type was inferred, switch the profile to compare.',
    ],
    useCases: ['Budget planning', 'Pre-proposal estimates', 'Investment planning'],
    faq: [
      {
        q: 'Are these fixed prices?',
        a: 'No—indicative premium boutique bands (TRY, excl. VAT). The final price is set per project: we listen to your request, define the scope and task list, estimate effort, assemble an outsourced team for the project, and send a price calculated from that effort and cost in a written proposal.',
      },
      {
        q: 'What project types are covered?',
        a: 'Corporate/marketing website, SaaS/MVP, mobile product, e-commerce, and general/custom software (ERP, CRM, internal tools) profiles—each with MVP, platform, UI/UX, and maintenance lines.',
      },
      {
        q: 'Do “corporate site” and “corporate website” give different prices?',
        a: 'No. Corporate site, corporate website and the Turkish equivalents map to the same profile (corporate / marketing website). If a description mixes several types or the type is unclear, the result says so and asks you to pick the profile.',
      },
    ],
    example: {
      summary: 'SaaS project',
      results: [
        formatProjectCostBandLine('saas_mvp', 'mvp', 'en'),
        formatProjectCostBandLine('saas_mvp', 'platform', 'en'),
      ],
    },
  },
  'color-palette-generator': {
    segment: 'Designers & brand owners',
    title: 'AI Color Palette Generator',
    tagline: 'HEX palettes that match your brand mood',
    intro:
      'Describe your brand mood (modern, warm, natural…) and get ready-to-use HEX color palettes for web and brand work.',
    inputs: {
      mood: { label: 'Brand mood', placeholder: 'e.g. modern, warm, minimal' },
    },
    seo: {
      title: 'Color Palette Generator — Free Brand Colors',
      description:
        'Generate HEX color palettes for your brand mood—free tool for web and identity design.',
      keywords: ['color palette generator', 'brand colors', 'hex palette', 'website color scheme'],
    },
    howItWorks: [
      'Describe brand mood in a few words.',
      'Click generate.',
      'Pick from HEX palette options.',
    ],
    useCases: ['Website design', 'Brand identity', 'Decks and moodboards'],
    faq: [
      { q: 'How do I use the palettes?', a: 'Copy HEX codes into Figma, Webflow, or your codebase.' },
      { q: 'How many palettes?', a: 'Several distinct moods per generation.' },
    ],
    example: {
      summary: 'modern',
      results: [
        '#0A0A0A • #F5F5F5 • #3B82F6 • #10B981 — minimal & clean',
        '#0D1117 • #58A6FF • #238636 • #F78166 — tech & modern',
      ],
    },
  },
  'ats-cv-analyzer': {
    segment: 'Job seekers & career changers',
    title: 'ATS CV Score & Improvement Tool',
    tagline: 'Score your resume against the job posting',
    intro:
      'Paste your CV and target job description—get an ATS compatibility score, missing keywords, and bullet rewrites in seconds. Free, no signup.',
    inputs: {
      cv: { label: 'CV / resume text', placeholder: 'Paste experience, skills, and education sections here...' },
      jobDescription: {
        label: 'Job description (optional)',
        placeholder: 'Paste the job ad for a sharper match score.',
      },
    },
    seo: {
      title: 'ATS CV Check & Scoring — Free AI Resume Analysis',
      description:
        'Analyze your CV for ATS compatibility for free. Score, keyword gaps, and improvement tips—tailored to the job posting.',
      keywords: ['ats resume checker', 'cv score', 'resume analyzer', 'ats compatibility', 'resume keywords'],
    },
    howItWorks: [
      'Paste your CV text.',
      'Add the job description if you have it.',
      'Review score, gaps, and improved bullet suggestions.',
    ],
    useCases: [
      'Pre-application CV check',
      'Keyword optimization per role',
      'Improve ATS pass rate',
    ],
    faq: [
      {
        q: 'What drives the score?',
        a: 'Keyword match to the job ad (~50%), role fit, bullet structure, and clarity. Missing critical keywords lower the score—we do not inflate it.',
      },
      {
        q: 'Do you artificially boost scores?',
        a: 'No. If keywords like "GA4" are missing from your CV, the score will be lower on purpose.',
      },
      { q: 'Is my CV stored?', a: 'No—text is used only for live analysis.' },
      {
        q: 'Can I use it without a job ad?',
        a: 'Yes—you get general CV quality feedback; ATS job matching cannot be scored without an ad.',
      },
    ],
    example: {
      summary: 'Digital marketing · SEO role',
      results: [
        'ATS Score: 52/100 — 4 of 10 critical skills from the ad appear in the CV (40%). Missing: "GA4", "CRO", "A/B test", "SQL".\n\nRewrite (only if you truly have this experience):\n• You may add GA4 to an existing Google Ads bullet—only if you actually used it.',
      ],
    },
  },
  'job-application-writer': {
    segment: 'Job seekers',
    title: 'Job-Tailored CV Bullets & Cover Letter Generator',
    tagline: 'ATS-friendly copy for each application',
    intro:
      'Enter your target role, experience, and job ad—get tailored CV bullets and cover letter drafts ready to paste. Built for applications, not generic profiles.',
    inputs: {
      role: { label: 'Target role / title', placeholder: 'e.g. Senior Frontend Developer' },
      experience: {
        label: 'Experience & skills',
        placeholder: 'e.g. 4 years React, TypeScript, team lead, fintech products',
      },
      jobDescription: {
        label: 'Job description',
        placeholder: 'Paste the full job posting...',
      },
    },
    seo: {
      title: 'AI CV & Cover Letter Writer — Free, Job-Tailored',
      description:
        'Generate ATS-friendly CV bullets and cover letters matched to the job ad. Free AI resume writing—no account.',
      keywords: ['ai resume writer', 'cover letter generator', 'tailored cv', 'job application writer', 'ats resume'],
    },
    howItWorks: [
      'Enter target role and your experience.',
      'Paste the job description.',
      'Copy CV bullets and cover letter drafts.',
    ],
    useCases: [
      'Customize every application',
      'Speed up cover letter writing',
      'Match ATS keywords to the posting',
    ],
    faq: [
      {
        q: 'How is this different from the LinkedIn About tool?',
        a: 'This produces application-specific bullets and cover letters tied to a job ad.',
      },
      { q: 'Which language?', a: 'Matches the language of your inputs—Turkish or English.' },
      { q: 'Is it free?', a: 'Yes, no signup required.' },
    ],
    example: {
      summary: 'Senior Frontend · fintech posting',
      results: [
        'CV bullet:\n• Built payment UI flows in React and TypeScript; cut page load time 40% and reduced checkout drop-off.\n\nCover opening:\nI want to bring my fintech frontend experience to your team—performance, reliability, and clean UX.',
      ],
    },
  },
  'presentation-outline-generator': {
    segment: 'Founders & teams',
    title: 'Presentation & Pitch Deck Outline Generator',
    tagline: 'Slide titles + speaker notes, ready to paste',
    intro:
      'Describe your topic and audience—get an 8–12 slide outline with headlines and speaker notes. Paste into PowerPoint, Google Slides, or Gamma.',
    inputs: {
      topic: { label: 'Presentation topic', placeholder: 'e.g. Q3 growth plan / Seed fundraise' },
      audience: { label: 'Audience', placeholder: 'e.g. investors, sales prospects, internal team' },
      purpose: {
        optionLabels: ['Investor pitch', 'Sales deck', 'Training / workshop', 'Corporate report'],
      },
    },
    seo: {
      title: 'AI Presentation Outline — Free Pitch Deck Draft',
      description:
        'Create presentation and pitch deck outlines with free AI—slide titles and speaker notes, no signup.',
      keywords: ['presentation ai', 'pitch deck outline', 'slide outline generator', 'deck draft'],
    },
    howItWorks: [
      'Define topic and audience.',
      'Pick presentation type.',
      'Copy the slide-by-slide outline.',
    ],
    useCases: ['Investor meetings', 'Sales demos', 'Internal strategy reviews'],
    faq: [
      {
        q: 'Does it export PowerPoint?',
        a: 'It outputs text—you paste into PowerPoint, Slides, or Gamma.',
      },
      { q: 'How many slides?', a: 'Typically 8–12 slides with a clear narrative arc.' },
      {
        q: 'Custom design from Figtures?',
        a: 'This is a free starter—talk to us for a fully designed pitch deck.',
      },
    ],
    example: {
      summary: 'Seed round · investors',
      results: [
        'Slide 1 — Hook\n"SMBs still run accounting in Excel. We change that in 10 minutes."\n\nSlide 2 — Problem\n• 2.3M SMBs on manual workflows\n• ~12 hours/week lost on average',
      ],
    },
  },
  'youtube-seo-generator': {
    segment: 'YouTubers & creators',
    title: 'YouTube Title & Description SEO Generator',
    tagline: 'Click-worthy titles + search-ready descriptions',
    intro:
      'Enter your video topic—get title variants, structured SEO description, and tag suggestions optimized for YouTube search and browse.',
    inputs: {
      topic: { label: 'Video topic', placeholder: 'e.g. Home coffee brewing guide' },
      niche: { label: 'Channel niche / audience', placeholder: 'e.g. coffee, lifestyle, ages 25–35' },
      videoLength: {
        optionLabels: ['Short (Shorts)', 'Medium (5–15 min)', 'Long (15+ min)'],
      },
    },
    seo: {
      title: 'YouTube Title Generator & Description SEO — Free AI',
      description:
        'Generate YouTube titles, descriptions, and tags with free AI—SEO-friendly and click-focused.',
      keywords: ['youtube title generator', 'youtube description', 'youtube seo', 'video title ideas'],
    },
    howItWorks: [
      'Enter the video topic.',
      'Add niche and length.',
      'Copy titles, description, and tags.',
    ],
    useCases: ['New uploads', 'Refresh old video SEO', 'Title A/B variants'],
    faq: [
      {
        q: 'Different from the Reels tool?',
        a: 'This targets YouTube search, descriptions, and tags—not short-form scripts.',
      },
      { q: 'Character limits?', a: 'Titles stay near 100 characters; descriptions follow SEO structure.' },
      { q: 'Free?', a: 'Yes.' },
    ],
    example: {
      summary: 'Home coffee brewing · medium length',
      results: [
        'Title ideas\n• Home Barista Coffee: 3 Mistakes to Avoid (5 Min Fix)\n• Why Your Filter Coffee Tastes Bitter — Simple Fix\n• Café Quality at Home for ₺200 — Worth It?',
      ],
    },
  },
  'instagram-caption-generator': {
    segment: 'Social managers & brands',
    title: 'Instagram Caption & Hashtag Generator',
    tagline: 'Post copy + bio line + hashtag set',
    intro:
      'Enter your post topic and brand tone—get scroll-stopping captions, a bio line idea, and reach-focused hashtags.',
    inputs: {
      topic: { label: 'Post topic', placeholder: 'e.g. New season collection launch' },
      brand: { label: 'Brand / account type', placeholder: 'e.g. fashion brand, personal blog, restaurant' },
      tone: {
        optionLabels: ['Warm', 'Professional', 'Playful', 'Informative'],
      },
    },
    seo: {
      title: 'Instagram Caption Generator — Free Hashtags & Bio Tool',
      description:
        'Create Instagram captions, bio lines, and hashtag sets with free AI—no signup.',
      keywords: ['instagram caption generator', 'instagram bio', 'hashtag suggestions', 'instagram copy'],
    },
    howItWorks: [
      'Describe the post topic.',
      'Set brand type and tone.',
      'Copy caption, hashtags, and bio suggestion.',
    ],
    useCases: ['Daily posting', 'Campaign launches', 'Bio refresh'],
    faq: [
      { q: 'How many hashtags?', a: 'Usually 15–25 mixed reach and niche tags.' },
      { q: 'Emojis?', a: 'Balanced to tone—not spammy.' },
      { q: 'Pair with Reels tool?', a: 'Yes—script in Reels tool, caption/hashtags here.' },
    ],
    example: {
      summary: 'Season launch · fashion brand',
      results: [
        'Caption (Warm):\nNew season is here 🖤\nMonths in the making—simple, bold, day to night.\nWhich piece are you trying first?\n\nHashtags:\n#newseason #fashion2026 #ootd #styleinspo #minimalstyle',
      ],
    },
  },
  'job-posting-writer': {
    segment: 'HR teams & employers',
    title: 'Job Posting Writer — ATS-Friendly Listings',
    tagline: 'Kariyer.net / LinkedIn format + inclusive language check',
    intro:
      'Enter role, company, and requirements—get an ATS-friendly job description, candidate profile, and inclusive-language checklist. Structured for paste, not a generic chat paragraph.',
    inputs: {
      role: { label: 'Role / title', placeholder: 'e.g. Senior Product Manager' },
      company: { label: 'Company & sector', placeholder: 'e.g. B2B SaaS · 45 people · Istanbul (hybrid)' },
      requirements: {
        label: 'Requirements & responsibilities',
        placeholder: 'e.g. 5+ years PM, B2B, roadmap, stakeholder management, English',
      },
      employmentType: {
        optionLabels: ['Full-time', 'Part-time', 'Internship', 'Freelance / project'],
      },
    },
    seo: {
      title: 'AI Job Posting Generator — Free ATS-Friendly Listing Writer',
      description:
        'Create job postings with free AI—ATS-friendly duties, requirements, and inclusive language checks. Ready for LinkedIn and job boards.',
      keywords: ['ai job posting generator', 'job description writer', 'ats job posting', 'inclusive job ad'],
    },
    howItWorks: [
      'Enter role and company details.',
      'List requirements and responsibilities.',
      'Copy posting, summary, and checklist.',
    ],
    useCases: ['SMB hiring', 'HR team speed-up', 'Standardized LinkedIn listings'],
    faq: [
      {
        q: 'How is this different from ChatGPT?',
        a: 'Structured sections, ATS keywords, must-have vs nice-to-have split, and an inclusive-language checklist—not one paragraph.',
      },
      { q: 'Will it invent salary?', a: 'No—it leaves a placeholder if you did not provide a range.' },
      { q: 'Language?', a: 'Matches your input—Turkish or English.' },
    ],
    example: {
      summary: 'Senior PM · B2B SaaS',
      results: [
        'Posting outline:\n• Responsibilities: 6 bullets\n• Requirements: must-have / nice-to-have\n• Inclusive language fixes included\n• ATS keywords: product roadmap, B2B SaaS',
      ],
    },
  },
  'quote-proposal-writer': {
    segment: 'Freelancers & agencies',
    title: 'Quote & Proposal Letter Generator',
    tagline: 'Scope, delivery, payment plan—client-ready',
    intro:
      'Enter service type, scope, and budget range—get a proposal letter, scope table, exclusions, and payment plan aligned with Turkish business practice.',
    inputs: {
      service: { label: 'Service / project type', placeholder: 'e.g. Corporate website + CMS integration' },
      scope: {
        label: 'Scope details',
        placeholder: 'e.g. 12 pages, blog module, bilingual, 2 revision rounds, 8-week delivery',
      },
      budget: { label: 'Budget range (₺)', placeholder: 'e.g. 120,000 – 150,000 TL + VAT' },
      clientType: { optionLabels: ['SMB', 'Enterprise', 'Startup', 'Public / NGO'] },
    },
    seo: {
      title: 'AI Quote & Proposal Generator — Free Project Proposal Template',
      description:
        'Create price quotes and proposal letters with free AI—scope table, payment plan, and exclusions for freelancers and agencies.',
      keywords: ['ai quote generator', 'proposal letter template', 'project quote writer', 'freelance proposal'],
    },
    howItWorks: [
      'Describe service and scope.',
      'Enter your budget range.',
      'Copy proposal, scope table, and payment plan.',
    ],
    useCases: ['Freelance quotes', 'Agency pre-sales', 'Standardized proposal format'],
    faq: [
      { q: 'Legally binding?', a: 'Draft text only—finalize VAT, signature, and contract terms with your process.' },
      { q: 'Invents pricing?', a: 'No—uses only the budget range you provide.' },
      { q: 'Exclusions included?', a: 'Yes—a clear exclusions section is always generated.' },
    ],
    example: {
      summary: 'Corporate web · SMB client',
      results: [
        'Proposal outline:\n• Cover + summary\n• Scope table: 5 rows\n• Total within your range + VAT note\n• Payment: 40/40/20\n• Exclusions: hosting, stock photos, third-party licenses',
      ],
    },
  },
  'motivation-letter-writer': {
    segment: 'Students & scholarship applicants',
    title: 'Motivation Letter Generator — Scholarships & Grad School',
    tagline: 'Why program · why you · your contribution',
    intro:
      'Enter target program, background, and motivation—get structured motivation letter drafts. Not a cover letter—focused on program fit and contribution.',
    inputs: {
      program: { label: 'Program / scholarship / school', placeholder: 'e.g. Fulbright · MIT Media Lab · Erasmus+' },
      background: {
        label: 'Background & achievements',
        placeholder: 'e.g. CS graduate, open source, social venture experience',
      },
      motivation: {
        label: 'Why this program?',
        placeholder: 'e.g. HCI research, accessible tech, European network',
      },
      tone: { optionLabels: ['Academic', 'Personal & story', 'Short & clear'] },
    },
    seo: {
      title: 'AI Motivation Letter Generator — Free Scholarship Application Draft',
      description:
        'Write motivation letters with free AI—for scholarships, grad school, and exchange programs. Structured drafts, not generic chat text.',
      keywords: ['motivation letter generator', 'scholarship motivation letter', 'grad school application letter'],
    },
    howItWorks: [
      'Describe program and background.',
      'Explain your motivation.',
      'Get three tone variants.',
    ],
    useCases: ['Scholarship applications', 'Grad school admissions', 'Erasmus and exchange programs'],
    faq: [
      {
        q: 'Different from cover letter tool?',
        a: 'Focuses on program fit, future goals, and contribution—not job applications.',
      },
      { q: 'Invents achievements?', a: 'No—uses only what you provide.' },
      { q: 'English programs?', a: 'Output language follows your input.' },
    ],
    example: {
      summary: 'Erasmus+ · social venture background',
      results: [
        'Academic tone:\nP1 — Why this program\nP2 — Evidence from your background\nP3 — Future goal + contribution to host institution',
      ],
    },
  },
  'meta-ads-copy-generator': {
    segment: 'Digital marketers & e-commerce',
    title: 'Meta Ad Copy Generator — Facebook & Instagram Ads',
    tagline: 'Primary text + headline + CTA + hook analysis',
    intro:
      'Enter product, audience, and campaign objective—get Meta Ads copy within character limits, three hook variants, and CTA suggestions.',
    inputs: {
      product: { label: 'Product / service', placeholder: 'e.g. Organic skincare set · 100% natural' },
      audience: {
        label: 'Target audience',
        placeholder: 'e.g. Women 25-40, Istanbul, skincare interested',
      },
      objective: {
        optionLabels: ['Sales / conversion', 'Traffic', 'Brand awareness', 'Lead generation'],
      },
      offer: { label: 'Offer / differentiator (optional)', placeholder: 'e.g. 20% off first order, free shipping' },
    },
    seo: {
      title: 'AI Meta Ad Copy Generator — Free Facebook & Instagram Ads Text',
      description:
        'Create Meta ad copy with free AI—primary text, headlines, descriptions, and CTAs within platform limits.',
      keywords: ['meta ad copy generator', 'facebook ad text', 'instagram ads copy', 'ai ad copywriter'],
    },
    howItWorks: [
      'Describe product and audience.',
      'Pick campaign objective.',
      'Copy three ad sets and hook analysis.',
    ],
    useCases: ['E-commerce launches', 'Lead gen campaigns', 'A/B test copy variants'],
    faq: [
      { q: 'Character limits?', a: 'Primary ~125 chars priority, headline ~40, description ~30—Meta Ads Manager aligned.' },
      { q: 'Invents discounts?', a: 'No—skips offers you did not provide.' },
      { q: 'Turkish ads?', a: 'Yes—natural Turkish consumer language, no hype spam.' },
    ],
    example: {
      summary: 'Skincare set · conversion objective',
      results: [
        'Set 1 — Curiosity hook\nPrimary: "Why does your skin dry out? Often one mistake."\nHeadline: Natural care set · 20% off\nCTA: Shop now',
      ],
    },
  },
};

export function getLocalizedTool(tool: ToolDefinition, lang: ToolsLang): ToolDefinition {
  if (lang === 'tr') return tool;
  const en = toolsEn[tool.id];
  if (!en) return tool;

  return {
    ...tool,
    segment: en.segment ?? tool.segment,
    title: en.title ?? tool.title,
    tagline: en.tagline ?? tool.tagline,
    intro: en.intro ?? tool.intro,
    inputs: tool.inputs.map((inp) => {
      const ein = en.inputs?.[inp.name];
      if (!ein) return inp;
      return {
        ...inp,
        label: ein.label ?? inp.label,
        placeholder: ein.placeholder ?? inp.placeholder,
        optionLabels: ein.optionLabels ?? inp.optionLabels,
      } satisfies LocalizedToolInput;
    }),
    seo: en.seo ?? tool.seo,
    howItWorks: en.howItWorks ?? tool.howItWorks,
    useCases: en.useCases ?? tool.useCases,
    faq: en.faq ?? tool.faq,
    example: en.example ?? tool.example,
  };
}

function enDeterministic(toolId: string, inputs: Record<string, string>): string[] {
  if (toolId === 'roi-calculator') {
    const budget = parseInt((inputs.budget || '').replace(/\D/g, ''), 10) || 10000;
    return [
      `Email marketing ROI: ₺${Math.round(budget * 4.2).toLocaleString('en-US')} — Highest return rate`,
      `Social ad return: ₺${Math.round(budget * 2.1).toLocaleString('en-US')} — ROAS: 2.1x`,
      `SEO investment (6 mo): ~${Math.round(budget * 0.02)}% organic traffic lift — long-term value`,
      `Estimated digital marketing return: ₺${Math.round(budget * 1.35).toLocaleString('en-US')}`,
    ];
  }
  if (toolId === 'color-palette-generator') {
    const m = (inputs.mood || 'your brand').trim();
    return [
      `#0A0A0A • #F5F5F5 • #3B82F6 • #10B981 — minimal & clean for "${m}"`,
      `#1A1A2E • #E94560 • #16213E • #0F3460 — bold & dynamic for "${m}"`,
      `#FAF3E0 • #2C3639 • #3F4E4F • #A27B5C — natural & premium for "${m}"`,
      `#0D1117 • #58A6FF • #238636 • #F78166 — tech & modern for "${m}"`,
    ];
  }
  return [];
}

export function runDeterministicTool(
  toolId: string,
  inputs: Record<string, string>,
  lang: ToolsLang,
): string[] {
  if (toolId === 'project-cost-estimator') {
    return estimateProjectCostFromInputs(inputs, lang).lines;
  }
  if (lang === 'en') {
    const enOut = enDeterministic(toolId, inputs);
    if (enOut.length) return enOut;
  }
  const fn = deterministicGenerators[toolId];
  return fn ? fn(inputs) : [];
}

/** Tool form → estimate: free-text type plus the optional explicit profile select ("auto" = infer). */
export function estimateProjectCostFromInputs(
  inputs: Record<string, string>,
  lang: ToolsLang,
): ProjectCostEstimate {
  const selected = isProjectCostProfile(inputs.profile) ? inputs.profile : null;
  return estimateProjectCost(inputs.project || '', lang, selected);
}

export function getProjectCostMethodologyNote(lang: ToolsLang): string {
  return estimateProjectCost('', lang).methodologyNote;
}

export function inputOptionLabel(field: LocalizedToolInput, optionValue: string, index: number): string {
  return field.optionLabels?.[index] ?? optionValue;
}
